import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Box,
  Typography,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  EasyCheckPageContainer,
  EasyCheckPageHeader,
  EasyCheckPageContent,
  EasyCheckDataGridBasic,
  EasyCheckButton,
  EasyCheckChip,
  EasyCheckIconButton
} from '../../common/Controls';
import BeneficiaryFormModal from './BeneficiaryFormModal';
import BeneficiaryImportModal from './BeneficiaryImportModal';
import { useBeneficiaryService } from '../../../services/beneficiaries/beneficiaryService';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';

const BeneficiarySetupPage = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const { snackbar, hideSnackbar, showSuccess, showError } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [searchQuery, setSearchQuery] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  
  const beneficiaryService = useBeneficiaryService();

  const reloadBeneficiaries = useCallback(async () => {
    try {
      setLoading(true);
      
      // Always load ALL records to enable client-side filtering
      console.log('📄 Loading ALL records for client-side filtering');
      const result = await beneficiaryService.getAllBeneficiaries(1, 10000);
      
      console.log('📄 API result:', result);
      
      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        
        const beneficiariesData = Array.isArray(data) ? data.map(beneficiary => ({
          id: beneficiary.Id || beneficiary.id,
          beneficiaryName: beneficiary.BeneficiaryName || beneficiary.beneficiaryName,
          companyId: beneficiary.CompanyId || beneficiary.companyId,
          isActive: beneficiary.IsActive !== undefined ? beneficiary.IsActive : beneficiary.isActive,
          createdDate: beneficiary.CreatedDate || beneficiary.createdDate,
          createdUser: beneficiary.CreatedUser || beneficiary.createdUser,
          modifiedDate: beneficiary.ModifiedDate || beneficiary.modifiedDate,
          modifiedUser: beneficiary.ModifiedUser || beneficiary.modifiedUser,
        })) : [];
        
        console.log('📄 Processed beneficiaries data:', beneficiariesData);
        
        setBeneficiaries(beneficiariesData);
        
        console.log('📄 Set beneficiaries:', beneficiariesData.length, 'records');
      } else {
        console.error('📄 API call failed:', result);
        setBeneficiaries([]);
        console.error('Failed to load beneficiaries:', result.message);
      }
    } catch (error) {
      console.error('📄 Error loading beneficiaries:', error);
      setBeneficiaries([]);
      console.error('Error loading beneficiaries:', error.message);
    } finally {
      setLoading(false);
    }
  }, [beneficiaryService]);

  useEffect(() => {
    if (!hasInitiallyLoaded) {
      reloadBeneficiaries();
      setHasInitiallyLoaded(true);
    }
  }, [reloadBeneficiaries, hasInitiallyLoaded]);

  // Prevent body scrollbar to ensure only DataGrid scrolls
  useEffect(() => {
    // Hide all possible scrollbars when component mounts
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow-x', 'hidden', 'important');
    document.body.style.setProperty('overflow-y', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-x', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
    
    // Also target the main app container
    const appContainer = document.getElementById('root');
    if (appContainer) {
      appContainer.style.setProperty('overflow', 'hidden', 'important');
    }
    
    // Restore scrollbar when component unmounts
    return () => {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow-x');
      document.body.style.removeProperty('overflow-y');
      document.documentElement.style.removeProperty('overflow-x');
      document.documentElement.style.removeProperty('overflow-y');
      if (appContainer) {
        appContainer.style.removeProperty('overflow');
      }
    };
  }, []);

  const handleAddBeneficiary = () => {
    setSelectedBeneficiary(null);
    setModalOpen(true);
  };

  const handleEditBeneficiary = useCallback((beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setModalOpen(true);
  }, []);

  const handleDeleteBeneficiary = useCallback(async (beneficiaryId) => {
    if (window.confirm('Are you sure you want to delete this beneficiary?')) {
      try {
        const result = await beneficiaryService.deleteBeneficiary(beneficiaryId);
        
        if (result.success || result.Success) {
          showSuccess('Beneficiary deleted successfully');
          reloadBeneficiaries();
        } else {
          showError(result.message || 'Failed to delete beneficiary');
        }
      } catch (error) {
        console.error('Error deleting beneficiary:', error);
        showError('Failed to delete beneficiary');
      }
    }
  }, [beneficiaryService, showSuccess, showError, reloadBeneficiaries]);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedBeneficiary(null);
  };

  const handleBeneficiarySave = async (beneficiaryData) => {
    setModalLoading(true);
    try {
      let result;
      
      if (selectedBeneficiary) {
        // Update existing beneficiary
        result = await beneficiaryService.updateBeneficiary(selectedBeneficiary.id, beneficiaryData);
      } else {
        // Create new beneficiary
        result = await beneficiaryService.createBeneficiary(beneficiaryData);
      }
      
      if (result.success || result.Success) {
        showSuccess(selectedBeneficiary ? 'Beneficiary updated successfully' : 'Beneficiary created successfully');
        setModalOpen(false);
        setSelectedBeneficiary(null);
        reloadBeneficiaries();
      } else {
        // Show the actual API error message
        showError(result.message || 'Failed to save beneficiary');
        // Don't throw error, just return to keep modal open for user to fix
      }
    } catch (error) {
      console.error('Error saving beneficiary:', error);
      // Show user-friendly error message
      showError(error.message || 'Failed to save beneficiary');
      // Don't throw error, just return to keep modal open
    } finally {
      setModalLoading(false);
    }
  };

  const handleImportOpen = () => {
    setImportModalOpen(true);
  };

  const handleImportClose = () => {
    setImportModalOpen(false);
  };

  const handleImportComplete = (results) => {
    setImportModalOpen(false);
    if (results && results.summary) {
      showSuccess(`Import completed: ${results.summary.successful} successful, ${results.summary.failed} failed`);
    }
    reloadBeneficiaries();
  };

  const getStatusColor = useCallback((isActive) => {
    return isActive ? 'primary' : 'error';
  }, []);

  const getStatusText = useCallback((isActive) => {
    return isActive ? 'Active' : 'Inactive';
  }, []);

  const columns = useMemo(() => [
    {
      field: 'beneficiaryName',
      headerName: 'Beneficiary Name',
      flex: 1,
      minWidth: 200,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, height: '100%', width: '100%' }}>
          <PersonIcon color="primary" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <EasyCheckChip
            variant="status"
            color={getStatusColor(params.value)}
          >
            {getStatusText(params.value)}
          </EasyCheckChip>
        </Box>
      ),
    },
    {
      field: 'createdUser',
      headerName: 'Created By',
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2">
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      width: 150,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2">
            {params.value ? new Date(params.value).toLocaleDateString() : '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      maxWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <EasyCheckIconButton
                variant="table"
                onClick={() => handleEditBeneficiary(params.row)}
              >
                <EditIcon fontSize="small" />
              </EasyCheckIconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <EasyCheckIconButton
                variant="danger"
                onClick={() => handleDeleteBeneficiary(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </EasyCheckIconButton>
            </Tooltip>
          </Stack>
        </Box>
      ),
    },
  ], [getStatusColor, getStatusText, handleEditBeneficiary, handleDeleteBeneficiary]);

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'space-between', p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridToolbarExport />
        </Box>
      </GridToolbarContainer>
    );
  };

  const rows = useMemo(() => {
    const allRows = Array.isArray(beneficiaries) ? beneficiaries.map((beneficiary, index) => ({
      id: beneficiary.id || index,
      beneficiaryName: beneficiary.beneficiaryName,
      isActive: beneficiary.isActive,
      createdUser: beneficiary.createdUser,
      createdDate: beneficiary.createdDate,
      modifiedDate: beneficiary.modifiedDate,
      modifiedUser: beneficiary.modifiedUser,
      companyId: beneficiary.companyId,
      ...beneficiary
    })) : [];

    if (!searchQuery.trim()) return allRows;

    const query = searchQuery.toLowerCase();
    return allRows.filter(row =>
      row.beneficiaryName && row.beneficiaryName.toLowerCase().includes(query)
    );
  }, [beneficiaries, searchQuery]);

  return (
    <EasyCheckPageContainer>
      <EasyCheckPageHeader
        title="Beneficiary Master"
        subtitle="Manage beneficiaries for your organization"
        sx={{
          py: 3, // Increase vertical padding (top and bottom)
        }}
        actions={[
          <TextField
            key="search"
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />,
          <EasyCheckButton
            key="import"
            buttonType="action"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleImportOpen}
          >
            Import
          </EasyCheckButton>,
          <EasyCheckButton
            key="add"
            buttonType="action"
            startIcon={<AddIcon />}
            onClick={handleAddBeneficiary}
          >
            Add Beneficiary
          </EasyCheckButton>
        ]}
      />

      <EasyCheckPageContent sx={{ pt: 0.5 }}>
        <EasyCheckDataGridBasic
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection={false}
          disableRowSelectionOnClick
          hideHorizontalScrollbar={true}
          autoWidth={true}
          scrollbarSize={0}
          rowHeight={64}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          columnHeaderHeight={56}
          slots={{
            toolbar: CustomToolbar,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'beneficiaryName', sort: 'asc' }],
            },
            columns: {
              columnVisibilityModel: {
                createdUser: false,
                createdDate: false,
              },
            },
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
              borderRadius: 0,
            },
            '& .MuiDataGrid-main': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f5f5f5',
              borderRight: 'none !important',
              borderLeft: 'none !important',
              padding: '16px 12px',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#F7F9FC',
              borderBottom: '1px solid #e8e8e8 !important',
              borderTop: 'none !important',
              borderRadius: '0 !important',
            },
            '& .MuiDataGrid-columnHeader': {
              borderRight: 'none !important',
              borderLeft: 'none !important',
              borderBottom: 'none !important',
              color: '#000000 !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'flex-start !important',
              borderRadius: '0 !important',
              '&:first-of-type': {
                borderTopLeftRadius: '0 !important',
              },
              '&:last-of-type': {
                borderTopRightRadius: '0 !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#000000 !important',
              fontWeight: '600',
              lineHeight: '1 !important',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex !important',
              alignItems: 'center !important',
              height: '100% !important',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #f5f5f5',
            },
          }}
        />
      </EasyCheckPageContent>

      <BeneficiaryFormModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleBeneficiarySave}
        beneficiary={selectedBeneficiary}
        loading={modalLoading}
      />

      <BeneficiaryImportModal
        open={importModalOpen}
        onClose={handleImportClose}
        onImportComplete={handleImportComplete}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transition="slide"
        showIcon={true}
      />
    </EasyCheckPageContainer>
  );
};

export default BeneficiarySetupPage; 